import { useEffect, useState } from "react";
import { api, cls, fmtPct } from "@/lib/api";

const METRICS = [
  { key: "sharpe", label: "Sharpe Ratio", desc: "Return per unit of total risk", fmt: (v) => v?.toFixed(2) ?? "—" },
  { key: "sortino", label: "Sortino Ratio", desc: "Return per unit of downside risk", fmt: (v) => v?.toFixed(2) ?? "—" },
  { key: "volatility", label: "Volatility", desc: "Annualised std dev", fmt: (v) => (v != null ? `${v.toFixed(2)}%` : "—") },
  { key: "max_drawdown", label: "Max Drawdown", desc: "Worst peak-to-trough loss", fmt: fmtPct, colored: true },
  { key: "beta", label: "Beta", desc: "Sensitivity to benchmark", fmt: (v) => v?.toFixed(2) ?? "—" },
  { key: "alpha", label: "Alpha", desc: "Excess return vs CAPM", fmt: fmtPct, colored: true },
  { key: "calmar", label: "Calmar Ratio", desc: "Return / MaxDD", fmt: (v) => v?.toFixed(2) ?? "—" },
  { key: "annualized_return", label: "Ann. Return", desc: "Annualised", fmt: fmtPct, colored: true },
];

export default function RiskMetrics({ code }) {
  const [m, setM] = useState(null);
  useEffect(() => {
    api.get(`/funds/${code}/risk-metrics`).then((r) => setM(r.data));
  }, [code]);

  return (
    <section
      data-testid="risk-metrics-section"
      className="bg-white border border-sif rounded-xl p-4 sm:p-6 card-shadow mb-6"
    >
      <div className="flex items-start justify-between flex-wrap gap-2.5 sm:gap-3 mb-4 sm:mb-5">
        <div>
          <div className="text-xs text-sif-muted uppercase tracking-wider font-semibold">Risk Metrics</div>
          <div className="text-base sm:text-lg font-semibold text-sif-primary mt-0.5">
            Sharpe, Sortino, Vol, MaxDD, Beta & Alpha
          </div>
        </div>
        {m && (
          <div className="text-[11px] text-sif-muted">
            {m.observations} obs · Rf = {m.risk_free_used}% · Benchmark: {m.benchmark}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 sm:gap-3">
        {METRICS.map((met) => {
          const v = m?.[met.key];
          const colorClass = met.colored ? cls(v) : "text-sif-primary";
          return (
            <div
              key={met.key}
              data-testid={`risk-tile-${met.key}`}
              className="rounded-lg border border-sif p-3 sm:p-4 bg-slate-50/60 card-hover flex flex-col justify-between"
            >
              <div className="text-[10px] text-sif-muted uppercase tracking-wider font-semibold truncate">{met.label}</div>
              <div className={`mt-1 sm:mt-1.5 text-base sm:text-lg font-bold font-mono ${colorClass}`}>
                {m ? met.fmt(v) : "…"}
              </div>
              <div className="text-[10px] text-sif-muted mt-1 truncate">{met.desc}</div>
            </div>
          );
        })}
      </div>

      {m && (
        <div className="mt-4 sm:mt-5 pt-3.5 sm:pt-4 border-t border-sif grid grid-cols-2 md:grid-cols-4 gap-3 text-[11px]">
          <MiniStat label="Best Day" value={fmtPct(m.best_day)} colored={cls(m.best_day)} />
          <MiniStat label="Worst Day" value={fmtPct(m.worst_day)} colored={cls(m.worst_day)} />
          <MiniStat label="Positive Days" value={`${m.positive_days_pct}%`} colored="text-sif-primary" />
          <MiniStat label="Downside Vol" value={`${m.downside_deviation}%`} colored="text-sif-primary" />
        </div>
      )}
    </section>
  );
}

function MiniStat({ label, value, colored }) {
  return (
    <div>
      <div className="text-sif-muted uppercase tracking-wider font-semibold">{label}</div>
      <div className={`mt-0.5 font-mono font-semibold ${colored}`}>{value}</div>
    </div>
  );
}
